import { ROUTE_PATTERNS } from "../shared/routing/appPaths";
import { ChatBubbleLeftRightIcon, CircleStackIcon, DocumentTextIcon, ShareIcon } from "@heroicons/react/24/outline";

/**
 * The top-level sections of the app shell (UX review §4). Each is a route; the sidebar
 * follows the active section.
 */
export type AppSectionId = "threads" | "workflows" | "sources" | "ontology";

export interface AppSection {
  readonly id: AppSectionId;
  readonly path: string;
  readonly labelKey: string;
  /** Whether the section has a sidebar; a page that needs only the full width has none. */
  readonly sidebar: boolean;
  readonly icon: typeof DocumentTextIcon;
}

export const APP_SECTIONS: readonly AppSection[] = [
  // The four pages of F19, in his order: Documents, Threads, Workflows, Data model.
  { id: "sources", path: ROUTE_PATTERNS.sources, labelKey: "sections.sources", sidebar: true, icon: DocumentTextIcon },
  { id: "threads", path: ROUTE_PATTERNS.threads, labelKey: "sections.threads", sidebar: true, icon: ChatBubbleLeftRightIcon },
  { id: "workflows", path: ROUTE_PATTERNS.workflows, labelKey: "sections.workflows", sidebar: true, icon: ShareIcon },
  // The Data model has one navigation level and a version picker in its header, no sidebar (F77).
  { id: "ontology", path: ROUTE_PATTERNS.ontology, labelKey: "sections.ontology", sidebar: false, icon: CircleStackIcon },
  // Nothing else (F19, ADR-0031): reviews are steps of Threads, whose list is their inbox
  // (D4); each memory is kept in its own small Thread and edited where it is used, and a
  // person's own are in their profile (D5); Data lives in the Data model's Instances page (F11, F77).
];

/** The sections whose pages sit beside a sidebar. */
export type SidebarSectionId = Extract<AppSectionId, "threads" | "workflows" | "sources">;

/** The section of a path if it has a sidebar, else `null`. */
export function sidebarSectionOfPath(pathname: string): SidebarSectionId | null {
  const id = sectionOfPath(pathname);
  const section = APP_SECTIONS.find((candidate) => candidate.id === id);
  return section?.sidebar === true ? (id as SidebarSectionId) : null;
}

/** The section a path belongs to, or `null` outside the sections (for example the profile). */
export function sectionOfPath(pathname: string): AppSectionId | null {
  const section = APP_SECTIONS.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`),
  );
  return section?.id ?? null;
}
